import { Sort } from '@angular/material/sort';
import { AccountNote } from '../services/models/account-notes.model';
import { AccountSearchResult } from '../services/models/account-search.model';
import { LicenceSummary, Subscription } from '../services/models/subscriptions.model';
import { AccountTeacher } from '../services/models/account-teachers.model';
import { LanguageType } from 'src/app/shared/objects/languages.object';
import { AccountStudent, StudentGroups } from '../services/models/account-students.model';
import { UsageReportApplications } from '../services/models/account-usage.model';

export interface AccountsState {
  accountLookup: AccountSearch;
  selectedAccount: Account;
}

export interface AccountSearch {
  searchResponse: AccountSearchResult[];
  loading: boolean;
  error: string | null;
  moreAccountsToLoad: boolean;
  accountSearchForm: {
    pageSize: number;
    searchTerm: string;
    selectedProperty: string;
    showAnonymizedAccounts: boolean;
  };
}

export interface Account {
  accountId: number;
  accountDetails: AccountDetails;
  loading: boolean;
  accountSubscriptions: AccountSubscription;
  accountNotes: AccountNotes;
  accountIsExporting: boolean;
  accountStatusUpdating: boolean;
  teachers: AccountTeachers;
  students: AccountStudents;
  usage: AccountUsage;
}

export interface AccountDetails {
  accountId: number;
  accountType: string;
  contactSms: boolean;
  contactWhatsapp: boolean;
  countryId: number;
  county?: string;
  enabled: boolean;
  isDeleted: boolean;
  institutionName: string;
  language: LanguageType;
  phoneNumber?: string;
  postcode?: string;
  studentCount: number;
  teacherCount: number;
  totalTimeSpentLearning: number;
  wordpressId: number;
  wordPressUrl?: string;
  accountCode?: string;
  hasClassroom: boolean;
  hasUnderstandingDyslexia: boolean;
  isAnonymised: boolean;
  accountAdmin: {
    adminFirstName: string;
    adminLastName: string;
    adminEmail: string;
    adminUid: string;
  };
  externalSchoolIdentifier?: string;
  externalSchoolParentIdentifier?: string;
  externalSchoolIdDataSourceId?: number;
  accountDetailsMeta: {
    schoolDataSources: [
      {
        id: number;
        name: string;
        prefix: string;
      }
    ];
  };
  address1?: string;
  address2?: string;
  city?: string;
  iltUid?: string;
  jobTitle?: string;
  dateNrsMigrated?: Date;
}

export interface AccountSubscription {
  loading: boolean;
  filter: string;
  sort: Sort;
  licenceSummaries: LicenceSummary[];
  subscriptions: Subscription[];
  filteredSubscriptions: Subscription[];
}

export interface AccountNotes {
  loading: boolean;
  automatic: boolean;
  filter: string;
  notes: AccountNote[];
  filteredNotes: AccountNote[];
}

export interface AccountTeachers {
  canCreateUdTrainee: boolean;
  canCreateClassroomUser: boolean;
  loading: boolean;
  filter: string[];
  teachers: AccountTeacher[];
  filteredTeachers: AccountTeacher[];
}

export interface AccountStudents {
  loading: boolean;
  pageNumber: number;
  moreStudentsToLoad: boolean;
  filter: string[];
  students: AccountStudent[];
  filteredStudents: AccountStudent[];
  groups: StudentGroups[];
}

export interface AccountUsage {
  loading: boolean;
  institutionName: string;
  accountId: number;
  accountType: string;
  totalTimeAllAppsSeconds: number;
  applications: UsageReportApplications[];
}

export const initialState: AccountsState = {
  accountLookup: {
    searchResponse: [],
    loading: false,
    error: null,
    moreAccountsToLoad: false,
    accountSearchForm: {
      pageSize: 11,
      searchTerm: '',
      selectedProperty: '',
      showAnonymizedAccounts: false
    }
  },
  selectedAccount: {
    accountId: 0,
    accountDetails: {} as AccountDetails,
    loading: true,
    accountSubscriptions: {
      loading: true,
      filter: 'Active',
      sort: { active: 'startDate', direction: 'asc' },
      licenceSummaries: [],
      subscriptions: [],
      filteredSubscriptions: []
    },
    accountNotes: {
      loading: true,
      automatic: false,
      filter: '',
      notes: [],
      filteredNotes: []
    },
    accountIsExporting: false,
    accountStatusUpdating: false,
    teachers: {
      canCreateUdTrainee: false,
      canCreateClassroomUser: false,
      loading: false,
      teachers: [],
      filteredTeachers: [],
      filter: ['Teacher', 'AccountAdmin', 'UnderstandingDyslexiaTrainee']
    },
    students: {
      loading: false,
      pageNumber: 1,
      moreStudentsToLoad: false,
      students: [],
      filteredStudents: [],
      filter: [],
      groups: []
    },
    usage: {
      loading: false,
      institutionName: '',
      accountId: 0,
      accountType: '',
      totalTimeAllAppsSeconds: 0,
      applications: []
    }
  }
};
